import { getTotalPrice } from '@shared/lib/money';
import { useMemo, useReducer } from 'react';

import { AeroexpressTrip, AeroexpressSelectedTrip } from '@websky/graphql';
import { getAeroexpressHasChanges, isRoundTrip } from './utils';
import { useToggleable } from '../../../hooks';

import { parse } from 'date-fns';

export interface Reducer {
	[key: string]: AeroexpressTrip;
}

interface SetAction {
	type: 'set';
	key: string;
	value: AeroexpressTrip;
}

interface SetOneAction {
	type: 'setOne';
	key: string;
	value: AeroexpressTrip;
}

interface ClearAction {
	type: 'clear';
}

type ActionTypes = SetAction | SetOneAction | ClearAction;

const reducer = (state: Reducer, action: ActionTypes) => {
	if (action.type === 'set') {
		if (isRoundTrip(action.value)) {
			let newState = { ...state };
			for (const key in newState) {
				newState = { ...newState, [key]: null };
			}

			return { ...newState, [action.key]: action.value };
		} else {
			let newState = { ...state };
			for (const key in newState) {
				if (newState[key] && isRoundTrip(newState[key])) {
					newState = { ...newState, [key]: null };
				}
			}

			return { ...newState, [action.key]: action.value };
		}
	} else if (action.type === 'clear') {
		let clearState = { ...state };
		for (const key in clearState) {
			clearState = { ...clearState, [key]: null };
		}

		return clearState;
	}

	return state;
};

export const useAeroexpressState = (trips: AeroexpressTrip[], selectedTrips: AeroexpressSelectedTrip[]) => {
	const initialState = useMemo(() => {
		let state: Reducer = {};

		trips.forEach(trip => {
			const key = trip.fromStation.direction;

			if (!state[key]) {
				const selectedTrip = selectedTrips.find(selectedTrip => selectedTrip.id === trip.id) ?? null;

				state = {
					...state,
					[key]: selectedTrip
				};
			}
		});

		return state;
	}, [trips, selectedTrips]);

	const [state, dispatch] = useReducer(reducer, initialState);
	const { isOpen: allPassengers, toggle: toggleAllPassengers } = useToggleable(false);

	const tripsByDirection = useMemo(() => {
		const tripsByDirectionMap = new Map<string, AeroexpressTrip[]>();

		trips.forEach(trip => {
			const key = trip.fromStation.direction;

			tripsByDirectionMap.set(key, [...(tripsByDirectionMap.get(key) ?? []), trip]);
		});

		return [...tripsByDirectionMap.entries()].sort(([_a, tripsA], [_b, tripsB]) => {
			const tripA = tripsA[0];
			const tripB = tripsB[0];

			if (isRoundTrip(tripA) || isRoundTrip(tripB)) {
				return 1;
			}

			const departureDateA = tripA?.departureDate
				? parse(tripsA[0].departureDate, 'HH:mm dd.MM.yyyy', new Date())
				: null;

			const departureDateB = tripB?.departureDate
				? parse(tripsB[0].departureDate, 'HH:mm dd.MM.yyyy', new Date())
				: null;

			return departureDateA > departureDateB ? 1 : -1;
		});
	}, [trips]);

	const selectedTripItems = useMemo(() => {
		let selectedItems: AeroexpressTrip[] = [];

		Object.keys(state).forEach(key => {
			const item = state[key];

			if (item) {
				selectedItems = [...selectedItems, item];
			}
		});

		return selectedItems;
	}, [state, trips]);

	const totalPrice = useMemo(() => {
		if (selectedTripItems.length) {
			return getTotalPrice(selectedTripItems, item => item.price);
		}

		return null;
	}, [selectedTripItems]);

	const hasChanges = getAeroexpressHasChanges(selectedTrips, selectedTripItems);

	const setTrip = (key: string, trip?: AeroexpressTrip) => {
		dispatch({ type: 'set', key, value: trip });
	};

	const clearTrips = () => {
		dispatch({ type: 'clear' });
	};

	return {
		state,
		tripsByDirection,
		selectedTripItems,
		totalPrice,
		allPassengers,
		hasChanges,
		setTrip,
		clearTrips,
		toggleAllPassengers
	};
};
